import React from 'react';
import { X, RotateCcw, Maximize2, Minimize2 } from 'lucide-react';
import TarsAvatar from './TarsAvatar';

interface ChatHeaderProps {
  onClose: () => void;
  onClear: () => void;
  onToggleExpand: () => void;
  isExpanded: boolean;
  isTyping?: boolean;
}

// The idle strip's frames are 320px square, the typing-tumble strip's are
// 168px. So the tumble animation is what caps how big the avatar can get
// before it turns to mush — on a retina display 140 CSS px already asks for
// 280 device px out of 168. Past that the typing state visibly softens, which
// is exactly the frame you're staring at while waiting for a reply.
const DOCKED_AVATAR = 110;
const EXPANDED_AVATAR = 140;

const ChatHeader: React.FC<ChatHeaderProps> = ({
  onClose,
  onClear,
  onToggleExpand,
  isExpanded,
  isTyping = false,
}) => {
  return (
    <div
      className={`relative bg-dev-bg border-b border-dev-border px-4 flex flex-col items-center text-center transition-[padding,gap] duration-300 ${
        isExpanded ? 'pt-8 pb-5 gap-2.5' : 'pt-6 pb-3.5 gap-1.5'
      }`}
    >
      <div className="absolute top-1.5 right-1.5 flex items-center gap-1">
        <button
          onClick={onToggleExpand}
          // Below lg the panel already fills the viewport, so there is nothing
          // for this to expand into.
          className="hidden lg:block p-2.5 rounded-lg hover:bg-dev-bg-card text-dev-text-muted hover:text-dev-accent-cyan transition-colors"
          aria-label={isExpanded ? 'Collapse chat' : 'Expand chat'}
          aria-pressed={isExpanded}
          title={isExpanded ? 'Collapse chat' : 'Expand chat'}
        >
          {isExpanded ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
        </button>
        <button
          onClick={onClear}
          className="p-2.5 rounded-lg hover:bg-dev-bg-card text-dev-text-muted hover:text-dev-text transition-colors"
          aria-label="Clear chat"
          title="Clear chat"
        >
          <RotateCcw size={16} />
        </button>
        <button
          onClick={onClose}
          className="p-2.5 rounded-lg hover:bg-dev-bg-card text-dev-text-muted hover:text-dev-accent transition-colors"
          aria-label="Close chat"
        >
          <X size={16} />
        </button>
      </div>
      <TarsAvatar size={isExpanded ? EXPANDED_AVATAR : DOCKED_AVATAR} isTyping={isTyping} />
      <h3 className={`font-bold font-mono text-dev-text ${isExpanded ? 'text-lg' : 'text-base'}`}>TARS</h3>
      <p className={`font-mono text-dev-text-dimmed ${isExpanded ? 'text-sm' : 'text-xs'}`}>
        <span className="text-dev-accent-cyan">honesty:</span> 100% <span className="text-dev-text-dimmed">|</span> <span className="text-dev-accent-cyan">humor:</span> 75%
      </p>
    </div>
  );
};

export default ChatHeader;
